import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useStateSelector } from 'src/redux';
import { Poppins } from 'src/common/Poppins';
import { GradientTextAction } from 'src/common/GradientText';
import { formatCurrency } from 'src/utils/misc';
import { CurrencyInputFormula } from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import { CarbonFlowSvg } from 'src/assets/dynamicSvg';
import { IntelVariable } from '../types';

const Card = styled.div<{ $maxHeight?: number }>`
  width: min(600px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  ${({ $maxHeight }) => $maxHeight != null && `max-height: ${$maxHeight}px;`}
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 37px 0 rgba(7, 46, 91, 0.12);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  flex-shrink: 0;
`;

// The variable rows scroll inside the bounded card; the header stays put.
const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

// The gradient base renders bold; lighten it to a medium weight.
const HeaderAction = styled(GradientTextAction)`
  font-weight: 500;
`;

const Row = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme, $active }) => ($active ? theme.colors.cultured : 'transparent')};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.cultured};
  }
`;

const Name = styled(Poppins)`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Editor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CurrencyField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconButton = styled.button<{ $gradient?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 1000px;
  cursor: pointer;
  background: ${({ theme, $gradient }) => ($gradient ? theme.colors.gradient : theme.colors.white)};
  border: ${({ theme, $gradient }) => ($gradient ? 'none' : `1px solid ${theme.colors.cflowerBlue}`)};

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gp2};

  &:hover {
    color: ${({ theme }) => theme.colors.gp1};
  }
`;

interface VariablesPopoverProps {
  variables: IntelVariable[];
  pending: boolean;
  onPopulate: (variableId: number, value: number, currency: boolean) => void;
  onAddWorkspaceVariable: () => void;
  onAddAssessmentVariable: () => void;
  /** When set, open straight into this variable's editor (a red formula chip was clicked). */
  initialEditId?: number | null;
  /** Caps the card height to the space available in the viewport; the list scrolls within. */
  maxHeight?: number;
}

export const VariablesPopover: React.FC<VariablesPopoverProps> = ({
  variables,
  pending,
  onPopulate,
  onAddWorkspaceVariable,
  onAddAssessmentVariable,
  initialEditId,
  maxHeight,
}) => {
  const colors = useStateSelector((state) => state.theme.colors);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<number | undefined>(undefined);
  const [draftCurrency, setDraftCurrency] = useState(true);

  const startEdit = (variable: IntelVariable) => {
    setEditingId(variable.id);
    setDraft(variable.value ?? undefined);
    setDraftCurrency(variable.currency);
  };

  useEffect(() => {
    if (initialEditId == null) return;
    const variable = variables.find((v) => v.id === initialEditId);
    if (variable) startEdit(variable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId]);

  const confirm = () => {
    if (editingId != null && draft != null) onPopulate(editingId, draft, draftCurrency);
    setEditingId(null);
  };

  return (
    <Card $maxHeight={maxHeight}>
      <Header>
        <Poppins px={16} weight={500} color="cflowerBlue" css="text-transform: uppercase;">
          Variables
        </Poppins>
        <Actions>
          <HeaderAction px={14} onClick={onAddWorkspaceVariable}>
            + Workspace variable
          </HeaderAction>
          <HeaderAction px={14} onClick={onAddAssessmentVariable}>
            + Assessment variable
          </HeaderAction>
        </Actions>
      </Header>

      <List>
      {variables.map((variable) => {
        const editing = editingId === variable.id;
        return (
          <Row key={variable.id} $active={editing}>
            <CarbonFlowSvg size={20} color={variable.scope === 'workspace' ? colors.brightBlue : undefined} />
            <Name px={14} weight={500} color="cflowerBlue">
              {variable.name}
            </Name>
            {editing ? (
              <Editor>
                <CurrencyField>
                  <Poppins px={14} weight={500} color="cflowerBlue">
                    Currency
                  </Poppins>
                  <Input
                    type="checkbox"
                    className="switch switch--small"
                    checked={draftCurrency}
                    onChange={(e) => setDraftCurrency(e.target.checked)}
                    aria-label="Currency"
                  />
                </CurrencyField>
                <CurrencyInputFormula
                  currency={draftCurrency}
                  value={draft ?? ''}
                  autoFocus
                  onValueChange={({ floatValue }) => setDraft(floatValue)}
                />
                <IconButton
                  type="button"
                  aria-label="Save value"
                  $gradient
                  disabled={pending || draft == null}
                  onClick={confirm}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4.16667 9.93083L8.10333 13.75L15.8333 6.25"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
                <IconButton type="button" aria-label="Cancel" onClick={() => setEditingId(null)}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 5L15 15M15 5L5 15"
                      stroke={colors.cflowerBlue}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Editor>
            ) : variable.value != null ? (
              <ValueRow>
                <Poppins px={14} weight={500} color="prussianBlue">
                  {variable.currency ? formatCurrency(variable.value) : variable.value.toLocaleString()}
                </Poppins>
                <EditButton type="button" aria-label={`Edit ${variable.name}`} onClick={() => startEdit(variable)}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 3.5L16.5 6.5M3 17H6L15.5 7.5C16.3 6.7 16.3 5.3 15.5 4.5C14.7 3.7 13.3 3.7 12.5 4.5L3 14V17Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </EditButton>
              </ValueRow>
            ) : (
              <ValueRow>
                <GradientTextAction px={14} onClick={() => startEdit(variable)} weight={500}>
                  Add Value
                </GradientTextAction>
                <EditButton
                  type="button"
                  aria-label={`Add value for ${variable.name}`}
                  onClick={() => startEdit(variable)}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 3.5L16.5 6.5M3 17H6L15.5 7.5C16.3 6.7 16.3 5.3 15.5 4.5C14.7 3.7 13.3 3.7 12.5 4.5L3 14V17Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </EditButton>
              </ValueRow>
            )}
          </Row>
        );
      })}
      </List>
    </Card>
  );
};
